import { gql } from '@/lib/api';

/** OS-1720 — conteos por estado del ciclo de vida (`planStatus`), nunca por plan. */
export interface PlatformSummary {
  totalTenants: number;
  pilotTenants: number;
  trialTenants: number;
  /** Clientes de pago al día. */
  activeTenants: number;
  pastDueTenants: number;
  suspendedTenants: number;
  cancelledTenants: number;
  /**
   * OS-1723 — MRR real: suma exacta del MRR de cada tenant activo o en mora, calculado de sus líneas
   * de suscripción. Este bloque es null si el rol no tiene `platform.billing.read` (OS-1717).
   */
  totalMrr: number | null;
  /** MRR potencial: líneas de tenants en piloto o prueba. */
  potentialMrr: number | null;
  payingTenants: number | null;
  /** MRR real / tenants que pagan. */
  arpa: number | null;
  /** MRR real por módulo (suma lo mismo que `totalMrr`). */
  mrrByModule: { moduleKey: string; mrr: number }[] | null;
  /** ISO 4217 (COP). */
  currency: string;
}

/** OS-1724 — `low | medium | high`, o sin puntaje: `new` (gracia), `no_data`, `not_operating`. */
export type ChurnRisk = 'low' | 'medium' | 'high' | 'new' | 'no_data' | 'not_operating';

export interface TenantHealthScore {
  tenantId: string;
  tenantName: string;
  planTier: string;
  planStatus: string;
  daysWithActivity: number;
  evaluationDays: number;
  /** null: sin registros de uso (no es cero). */
  avgDau: number | null;
  avgDau7: number | null;
  trend: 'up' | 'down' | 'flat' | null;
  usageDrop: boolean;
  usageAvailable: boolean;
  lastActivity: string | null;
  /** null cuando no aplica (ver `churnRisk`). */
  healthScore: number | null;
  churnRisk: ChurnRisk;
  createdAt: string;
}

export interface UsageMetric {
  key: string;
  label: string;
  /** null = no disponible; nunca se muestra como 0. */
  value: number | null;
  unavailableReason: string | null;
}

export interface TenantHealth {
  health: TenantHealthScore;
  usage: UsageMetric[];
}

const SUMMARY_QUERY = `
  query PlatformSummary {
    platformSummary {
      totalTenants pilotTenants trialTenants activeTenants pastDueTenants
      suspendedTenants cancelledTenants totalMrr potentialMrr payingTenants arpa
      mrrByModule { moduleKey mrr } currency
    }
  }
`;

const HEALTH_QUERY = `
  query TenantHealthScores($limit: Int) {
    tenantHealthScores(limit: $limit) {
      tenantId tenantName planTier planStatus
      daysWithActivity evaluationDays avgDau avgDau7 trend usageDrop usageAvailable lastActivity
      healthScore churnRisk createdAt
    }
  }
`;

export async function getPlatformSummary(): Promise<PlatformSummary> {
  const data = await gql<{ platformSummary: PlatformSummary }>(SUMMARY_QUERY);
  return data.platformSummary;
}

/** OS-1724 — todos los tenants (sin `limit`). */
export async function getTenantHealthScores(limit?: number): Promise<TenantHealthScore[]> {
  const data = await gql<{ tenantHealthScores: TenantHealthScore[] }>(HEALTH_QUERY, { limit: limit ?? null });
  return data.tenantHealthScores;
}

export const tenantHealthQueryKey = (tenantId: string): readonly unknown[] => ['tenant-health', tenantId];

/** OS-1724 — salud y uso de 30 días de un tenant (`platform.reports.read`). */
export async function getTenantHealth(tenantId: string): Promise<TenantHealth> {
  const data = await gql<{ platformTenantHealth: TenantHealth }>(
    `query TenantHealth($tenantId: ID!) { platformTenantHealth(tenantId: $tenantId) {
      health {
        tenantId tenantName planTier planStatus daysWithActivity evaluationDays avgDau avgDau7 trend
        usageDrop usageAvailable lastActivity healthScore churnRisk createdAt
      }
      usage { key label value unavailableReason }
    } }`,
    { tenantId },
  );
  return data.platformTenantHealth;
}
